import { useEffect } from 'react'
import { getCookie } from 'cookies-next'
import { IncomingMessage, ServerResponse } from 'http'
import { AsH2, HeadingXS, LabelMD } from '@/atoms/Text/Text'
import { LOGIN_STARTED_ORIGIN } from '@/constants/cookies'
import { paths } from '@/constants/paths'
import { RegionContextProvider } from '@/contexts/RegionContext'
import { AppFeatures, ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { ContentOnlyLayout } from '@/layouts/ContentOnlyLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getContentfulClient } from '@/services/ApolloClient'
import { getCollection, GUILD_MEMBERSHIP_NUMBERS_ID } from '@/services/CmsService/Collection'
import { getGuildPromotion, Promotion } from '@/services/GuildPromotionService'
import { getGuildPricingTicketsOption, GuildPricingTicketsOption } from '@/services/GuildSignupPageService'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { getGuildSale, GuildSale } from '@/services/SaleService'
import { Maybe } from '@/types/codegen-contentful'
import { isGuildMostlySupported, shouldShowAvailabilityWarning } from '@/utils/GuildUtil'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getStringFromSessionStorage, removeFromSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GuildPricingView } from '@/views/GuildPricingView'
import { GuildSignupContextProvider } from '@/views/GuildSignupView/GuildSignupContext'

interface GuildPricingPageProps {
  locale: string
  preview: boolean
  slug?: string
  promotion: Promotion | null
  sale: GuildSale | null
  canShowFreeOption: boolean
  members?: number
  lastUpdated?: string
  experiments: Record<keyof AppFeatures, unknown>
  isOnlyBasic?: boolean
  shouldShowAvailabilityWarning: boolean
  region: string | null
  guildPricingTicketsOption?: Maybe<GuildPricingTicketsOption>
}

const GuildPricingPage: NextPageWithLayout<GuildPricingPageProps> = ({
  locale,
  slug,
  promotion,
  sale,
  canShowFreeOption,
  experiments,
  members,
  lastUpdated,
  isOnlyBasic = false,
  shouldShowAvailabilityWarning = false,
  region,
  guildPricingTicketsOption,
}) => {
  const { t } = useTranslate('guild')
  const title = t('angelGuildPricingTitle', 'Angel Guild Membership Pricing | Angel')
  const description = t(
    'angelStudiosGuildJoinInfoMetaDescriptionV2',
    'As an Angel Guild member, you can vote on upcoming shows, receive free tickets to every theatrical release, gain early access to shows, receive merchandise discounts, and continue to amplify light.',
  )

  useEffect(() => {
    if (getStringFromSessionStorage('guildCheckoutOrigin')) {
      removeFromSessionStorage('guildCheckoutOrigin')
    }
  }, [])

  const getHeading = () => (
    <div className="flex flex-col items-center gap-3 px-4 pt-4 text-center md:gap-5 md:pt-0">
      <HeadingXS
        weight="bold"
        color={'black'}
        id="plan-selection"
        as={AsH2}
        className="md:photon-display md:photon-heading-md lg:photon-heading-xl max-w-[789px] text-pretty md:text-[60px] md:leading-[3.5rem]"
      >
        {t('joinMovementChangingEntertainment', 'Join the Movement Changing Entertainment')}
      </HeadingXS>
      <LabelMD weight="semibold" color="core-gray-600" className="text-pretty">
        {t('supportFilmmakersCancelAnytime', 'Support films & filmmakers that share your values. Cancel anytime.')}
      </LabelMD>
    </div>
  )

  return (
    <>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1736351150/angel-studios/guild/amgel-guild-open-graph-241030.webp',
          cloudinaryTransformation: 'b_rgb:000000',
          description,
          locale,
          path: `${paths.guild.pricing}`,
          title,
        })}
      />
      <RegionContextProvider region={region}>
        <GuildSignupContextProvider
          slug={slug}
          promotion={promotion}
          sale={sale}
          experiments={experiments}
          members={members}
          lastUpdated={lastUpdated}
          shouldShowAvailabilityWarning={shouldShowAvailabilityWarning}
          region={region}
          guildPricingTicketsOption={guildPricingTicketsOption}
        >
          <GuildPricingView heading={getHeading()} canShowFreeOption={canShowFreeOption} isOnlyBasic={isOnlyBasic} />
        </GuildSignupContextProvider>
      </RegionContextProvider>
    </>
  )
}

GuildPricingPage.getLayout = (page) => {
  return <ContentOnlyLayout>{page}</ContentOnlyLayout>
}

export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, params, preview, query, region, req, res }) => {
    const slug = (params?.slug as string) ?? 'pricing'

    const guildStatus = await getGuildStatus(federationClient)

    if (guildStatus?.isGuildMember) {
      // Preserve tv-post-auth and UTM params when redirecting Guild members
      const tvPostAuth = query['tv-post-auth']
      const utmSource = query.utm_source
      const utmMedium = query.utm_medium
      const utmCampaign = query.utm_campaign

      const params = new URLSearchParams()
      if (tvPostAuth) params.set('tv-post-auth', tvPostAuth as string)
      if (utmSource) params.set('utm_source', utmSource as string)
      if (utmMedium) params.set('utm_medium', utmMedium as string)
      if (utmCampaign) params.set('utm_campaign', utmCampaign as string)

      const queryString = params.toString()
      const destination = `/guild${queryString ? `?${queryString}` : ''}`

      return {
        redirect: {
          destination,
          permanent: false,
        },
      }
    }

    const isSupportLink = query.special === 'last-chance'

    const canShowFreeOption = getCookie(LOGIN_STARTED_ORIGIN, { req, res }) === 'home'

    const guildMembershipNumbers = await getCollection({ locale, preview }, GUILD_MEMBERSHIP_NUMBERS_ID)
    const members = Number(guildMembershipNumbers?.customData[0]?.numSubscribers.replace(/,/g, '')) ?? null
    const lastUpdated = guildMembershipNumbers?.customData[0]?.updatedAt ?? null

    const isOnlyBasic = !isGuildMostlySupported(region)

    const opts = { locale, preview }
    const contentfulClient = getContentfulClient({ preview, locale })
    const [promotion, sale, guildPricingTicketsOption, experiments, translations] = await Promise.all([
      getGuildPromotion({ opts: { locale, preview }, isSupportLink, federationClient }),
      getGuildSale({ opts: { preview, locale }, federationClient }),
      getGuildPricingTicketsOption({ contentfulClient, opts }),
      runExperiments({ req, res, isOnlyBasic, canShowFreeOption }),
      loadTranslations(locale, ['common', 'guild', 'join-the-guild', 'guild-benefits']),
    ])

    return {
      props: {
        locale,
        preview,
        promotion,
        sale,
        slug,
        canShowFreeOption,
        experiments,
        isOnlyBasic,
        shouldShowAvailabilityWarning: shouldShowAvailabilityWarning(region),
        members,
        lastUpdated,
        region,
        guildPricingTicketsOption,
        ...translations,
      },
    }
  },
)

export default GuildPricingPage

async function runExperiments({
  req,
  res,
  isOnlyBasic,
  canShowFreeOption,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  isOnlyBasic: boolean
  canShowFreeOption: boolean
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = []

  if (!isOnlyBasic) {
    expConfigs.push({ experimentKey: 'guild_price_anchor', defaultValue: 'OFF' })
  }

  if (canShowFreeOption) {
    expConfigs.push({ experimentKey: 'free-signup-flow-watchlist', defaultValue: false })
  }

  return await runExperimentsOnServer(req, res, expConfigs)
}
